import { router } from '@inertiajs/react'
import { Component, Fragment, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import SurveyLayout from '@/layouts/SurveyLayout'
import PageHeader from '@/components/PageHeader'
import SurveyQrCode from '@/components/SurveyQrCode'
import type { SurveyDetail, QuestionResult, ResponseTrend, FacilitatorFilter, IndividualResponse, TextAnswer } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'
import { reportClientError } from '@/lib/chunkRecovery'

type PdfStatus = 'idle' | 'pending' | 'ready' | 'failed'

const Chart = lazy(() => import('react-apexcharts'))

// Distribution charts size themselves from the number of answer options so that
// a bar is the same thickness whether the question is yes/no or a 7-point scale.
const CATEGORY_SLOT_PX = 40
const CHART_CHROME_PX = 56

// Trend chart: beyond this many days the bars compress until printed values
// collide, so the chart gets an explicit width and scrolls instead.
const TREND_SCROLL_AFTER = 14
const TREND_MIN_BAR_PX = 44

// The chart library is code-split, so its chunk is fetched on demand. If that
// fetch fails the promise rejects, Suspense never settles, and the chart stays
// blank forever — with a hard refresh the only way back. The likeliest trigger
// is a deploy rotating asset hashes underneath an already-open tab, which
// reproduces in production but not against the dev server. An unguarded lazy()
// is a latent permanent-blank either way, so it gets a boundary.
//
// NOTE: this is a class component, against the repo's functional-only rule.
// React provides no hook equivalent of componentDidCatch — error boundaries are
// the one documented exception.
class ChartBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error) {
    // This used to claim it was "surfaced to Sentry via the global handler". It
    // wasn't: Sentry is server-side only here, so every chart failure in
    // production went to a console nobody could read, which is why this took a
    // month to explain. Report it where we can actually see it.
    console.error('[results] chart failed to load', error)
    reportClientError('chart_render_failed', error.message, { name: error.name })
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="h-32 flex flex-col items-center justify-center gap-2 text-sm text-base-content/60">
          <span>This chart couldn&apos;t be loaded.</span>
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => window.location.reload()}>
            Reload the page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

interface SessionFilter {
  id: string
  response_count: number
}

interface ResultsProps {
  survey: SurveyDetail
  question_results: QuestionResult[]
  total_responses: number
  suppressed?: boolean
  min_display_responses?: number
  responses_over_time: ResponseTrend[] | null
  facilitators?: FacilitatorFilter[]
  facilitator_filter?: string | null
  has_unattributed?: boolean
  sessions?: SessionFilter[]
  session_filter?: string | null
  groups?: SessionFilter[]
  group_filter?: string | null
  individual_responses?: IndividualResponse[] | null
  can_see_individual_responses?: boolean
}

const POLL_INTERVAL = 60_000

import { SCALE_PRESETS } from '@/types'

function questionTypeLabel(q: QuestionResult['question']): string {
  if (q.question_type === 'scale') {
    if (q.scale_labels) return `Custom (1–${q.scale_points})`
    if (q.scale_preset && SCALE_PRESETS[q.scale_preset]) return SCALE_PRESETS[q.scale_preset].label
    return `Scale (1–${q.resolved_points})`
  }
  if (q.question_type === 'open_text') return 'Open Text'
  if (q.question_type === 'single_choice') return 'Single Choice'
  return q.question_type
}

function timeAgo(seconds: number): string {
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const mins = Math.floor(seconds / 60)
  return `${mins}m ago`
}

function SuppressedNotice({ min, total, filtered }: { min: number; total: number; filtered: boolean }) {
  return (
    <div className="card bg-base-100">
      <div className="card-body items-center text-center py-16 space-y-4">
        <span className="iconify lucide--shield-check size-12 text-base-content/20" />
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">
            {filtered ? 'Not enough responses in this view' : 'Results are hidden until more responses arrive'}
          </h2>
          <p className="text-sm text-base-content/60 max-w-md">
            {filtered
              ? `This filter matches ${total} ${total === 1 ? 'response' : 'responses'}. To protect respondent anonymity, results for a group are only shown once it reaches ${min} responses. Try a broader filter.`
              : `${total} ${total === 1 ? 'response has' : 'responses have'} been collected so far. To protect respondent anonymity, aggregate results appear once at least ${min} people have responded.`}
          </p>
        </div>
      </div>
    </div>
  )
}

function DistributionChart({
  distribution,
  primaryColour,
  scaleLabels,
}: {
  distribution: Record<string, number>
  primaryColour: string
  scaleLabels?: (string | null)[] | null
}) {
  const keys = Object.keys(distribution)
  const values = Object.values(distribution)
  const total = values.reduce((a, b) => a + b, 0)

  // Build display labels: "1 — Poor", "2 — Fair", etc.
  const displayLabels = keys.map((key) => {
    const idx = parseInt(key, 10) - 1
    const label = scaleLabels?.[idx]
    return label ? `${key} — ${label}` : key
  })

  return (
    <ChartBoundary>
    <Suspense fallback={<div className="h-48 flex items-center justify-center"><span className="loading loading-spinner" /></div>}>
      <Chart
        type="bar"
        // Fixed slot PER CATEGORY rather than a fixed overall height. With a
        // constant 180px, a 2-option yes/no split its height across 2 slots and
        // drew bars ~2.5x thicker than a 5-point scale — the inconsistency the
        // team flagged (feedback #5). Height now follows the category count, so
        // bar thickness is identical on every question.
        height={keys.length * CATEGORY_SLOT_PX + CHART_CHROME_PX}
        options={{
          // Animations off: the results page live-reloads its props, and an
          // animated redraw in a throttled background tab can stall with the
          // bars stuck at zero size until a manual refresh.
          chart: { toolbar: { show: false }, background: 'transparent', animations: { enabled: false } },
          plotOptions: {
            bar: { horizontal: true, barHeight: '60%', borderRadius: 4 },
          },
          colors: [primaryColour],
          xaxis: {
            categories: displayLabels,
            labels: {
              style: { fontSize: '12px' },
              // The value axis counts responses — show whole numbers only, so
              // it can't be misread as a fractional score scale.
              formatter: (val: string) => {
                const n = Number(val)
                return Number.isInteger(n) ? String(n) : ''
              },
            },
            // Bumped from 11px/#9ca3af — the team couldn't read it (feedback #4).
            title: { text: 'Number of responses', style: { fontSize: '12px', fontWeight: 500, color: '#4b5563' } },
            min: 0,
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            labels: { style: { fontSize: '12px' } },
          },
          grid: { show: false },
          dataLabels: {
            enabled: true,
            style: { fontSize: '12px', fontWeight: 600 },
          },
          tooltip: {
            enabled: true,
            y: {
              formatter: (val: number) => {
                const pct = total > 0 ? ((val / total) * 100).toFixed(0) : '0'
                return `${val} responses (${pct}%)`
              },
            },
          },
        }}
        series={[{ name: 'Responses', data: values }]}
      />
    </Suspense>
    </ChartBoundary>
  )
}

function TrendChart({
  data,
  primaryColour,
}: {
  data: ResponseTrend[]
  primaryColour: string
}) {
  // Bars, not an area (D2). A smoothed area over a short fielding window renders
  // as a near-flat wash whose values only appear on hover, which read as "no
  // results" — the reason this section was mistaken for an empty page. Bars with
  // printed counts are legible standing still.
  return (
    <ChartBoundary>
    <Suspense fallback={<div className="h-32 flex items-center justify-center"><span className="loading loading-spinner" /></div>}>
      {/* Past ~14 days the bars would compress until the printed values collide.
          Give each bar a floor width and let the container scroll instead. */}
      <div className={data.length > TREND_SCROLL_AFTER ? 'overflow-x-auto' : undefined}>
      <Chart
        type="bar"
        height={160}
        width={data.length > TREND_SCROLL_AFTER ? data.length * TREND_MIN_BAR_PX : undefined}
        options={{
          chart: { toolbar: { show: false }, background: 'transparent', animations: { enabled: false } },
          colors: [primaryColour],
          plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
          xaxis: {
            categories: data.map((d) => d.date),
            labels: { style: { fontSize: '11px' }, rotate: -45, rotateAlways: data.length > 14 },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            labels: { style: { fontSize: '12px' } },
            min: 0,
            forceNiceScale: true,
          },
          grid: { borderColor: '#e5e7eb', strokeDashArray: 4 },
          // Values stay printed on every bar, always — reverting to hover past
          // some threshold would reintroduce exactly the "looks blank" problem
          // D2 exists to fix. Long fielding windows scroll horizontally instead
          // (see the wrapper below) so bars keep a legible width.
          dataLabels: {
            enabled: true,
            offsetY: -18,
            style: { fontSize: '11px', fontWeight: 600, colors: ['#4b5563'] },
          },
          tooltip: { x: { format: 'dd MMM yyyy' } },
        }}
        series={[{ name: 'Responses', data: data.map((d) => d.count) }]}
      />
      </div>
    </Suspense>
    </ChartBoundary>
  )
}

// Accent gauge showing a mean's position on the scale range — mirrors the PDF's
// "Average" marker so the two reports read identically.
function AverageGauge({ label, value, min, max, colour }: {
  label: string
  value: number
  min: number
  max: number
  colour: string
}) {
  if (value == null || value <= 0) return null
  const frac = max > min ? Math.min(1, Math.max(0, (value - min) / (max - min))) : 0
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-28 shrink-0 font-semibold">{label}</span>
      <div className="h-4 flex-1 overflow-hidden rounded bg-base-200">
        <div className="h-full rounded" style={{ width: `${frac * 100}%`, backgroundColor: colour }} />
      </div>
      <span className="w-12 text-end font-semibold tabular-nums" style={{ color: colour }}>
        {value.toFixed(2)}
      </span>
    </div>
  )
}

function NumericResult({ result, primaryColour, accentColour }: { result: QuestionResult; primaryColour: string; accentColour: string }) {
  const { distribution, mean, response_count } = result
  const points = result.question.resolved_points

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 text-sm">
        {/* Response count: darkened from /60 — the team read it as too faint to
            scan (feedback #4). */}
        <span className="text-base-content/70">
          {response_count} {response_count === 1 ? 'response' : 'responses'}
        </span>
        {mean != null && (
          <span className="font-semibold">
            {/* "Average", not "Mean" — one word for one number across screen,
                PDF and CSV (D3). There is no median in the product, so the
                statistical spelling bought nothing but jargon. */}
            Average: <span className="tabular-nums">{mean.toFixed(2)}</span>
          </span>
        )}
      </div>
      {mean != null && <AverageGauge label="Average" value={mean} min={1} max={points} colour={accentColour} />}
      {distribution && <DistributionChart distribution={distribution} primaryColour={primaryColour} scaleLabels={result.question.resolved_labels} />}
    </div>
  )
}

function RetrospectiveNumericResult({ result, survey }: { result: QuestionResult; survey: SurveyDetail }) {
  const { distribution, distribution_before, mean, mean_before, mean_shift, response_count } = result
  const labelBefore = survey.retrospective_label_before || 'Before'
  const labelAfter = survey.retrospective_label_after || 'After'
  const points = result.question.resolved_points

  // Lightened primary for the "before" series — matches the PDF's before-tint.
  const beforeColour = survey.before_tint_colour

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        {/* Darkened from /60 to match the single-scale variant (feedback #4). */}
        <span className="text-base-content/70">
          {response_count} {response_count === 1 ? 'response' : 'responses'}
        </span>
        {mean_before != null && (
          <span>
            {labelBefore}: <span className="font-semibold tabular-nums">{mean_before.toFixed(2)}</span>
          </span>
        )}
        {mean != null && (
          <span>
            {labelAfter}: <span className="font-semibold tabular-nums">{mean.toFixed(2)}</span>
          </span>
        )}
        {mean_shift != null && (
          <span className={`font-bold tabular-nums ${mean_shift > 0 ? 'text-success' : mean_shift < 0 ? 'text-error' : ''}`}>
            Shift: {mean_shift > 0 ? '+' : ''}{mean_shift.toFixed(2)}
          </span>
        )}
      </div>

      {/* Average markers — before (tint) and after (primary), colour-matched to the charts */}
      {(mean_before != null || mean != null) && (
        <div className="space-y-1.5">
          {mean_before != null && <AverageGauge label={`${labelBefore} avg`} value={mean_before} min={1} max={points} colour={beforeColour} />}
          {mean != null && <AverageGauge label={`${labelAfter} avg`} value={mean} min={1} max={points} colour={survey.primary_colour} />}
        </div>
      )}

      {/* Paired charts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-semibold text-base-content/50 mb-2">{labelBefore}</h4>
          {distribution_before && <DistributionChart distribution={distribution_before} primaryColour={beforeColour} scaleLabels={result.question.resolved_labels} />}
        </div>
        <div>
          <h4 className="text-xs font-semibold text-base-content/50 mb-2">{labelAfter}</h4>
          {distribution && <DistributionChart distribution={distribution} primaryColour={survey.primary_colour} scaleLabels={result.question.resolved_labels} />}
        </div>
      </div>
    </div>
  )
}

const REDACTION_TOKEN = '[redacted]'

// DOM selections are measured in UTF-16 code units; Ruby indexes strings by
// character. Convert before sending so an emoji earlier in a comment can't shift
// the span the server cuts.
function codePointOffset(value: string, codeUnitOffset: number): number {
  return Array.from(value.slice(0, codeUnitOffset)).length
}

// Pins the exact text the moderator was looking at, so the server can refuse to
// cut if the value moved. SubtleCrypto is available on the secure origins this
// admin UI runs on.
async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value)
  const hash = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// A comment rendered as offset-tagged runs. Splitting on the redaction token
// lets us style already-removed spans AND gives each run a known start offset in
// the original string, which is how a selection is translated back into
// character positions for the server.
function CommentRuns({ value, moderated }: { value: string; moderated: boolean }) {
  // Only tokenize when the answer has ACTUALLY been redacted. Otherwise a
  // respondent who legitimately typed "[redacted]" in their own words would have
  // it styled as though a moderator had removed something.
  if (!moderated) {
    return <span data-offset={0}>{value}</span>
  }

  const runs: { text: string; offset: number; redacted: boolean }[] = []
  let cursor = 0
  for (const piece of value.split(REDACTION_TOKEN)) {
    if (piece.length > 0) runs.push({ text: piece, offset: cursor, redacted: false })
    cursor += piece.length
    if (cursor < value.length) {
      runs.push({ text: REDACTION_TOKEN, offset: cursor, redacted: true })
      cursor += REDACTION_TOKEN.length
    }
  }
  return (
    <>
      {runs.map((run, i) =>
        run.redacted ? (
          <span key={i} className="bg-base-300 text-base-content/50 rounded px-1 select-none">
            {run.text}
          </span>
        ) : (
          <span key={i} data-offset={run.offset}>
            {run.text}
          </span>
        )
      )}
    </>
  )
}

function RedactableComment({
  answer,
  surveyId,
}: {
  answer: TextAnswer
  surveyId: number
}) {
  const ap = useAccountPath()
  const [pending, setPending] = useState<{ start: number; end: number; text: string } | null>(null)
  const [confirmingHide, setConfirmingHide] = useState(false)
  const [digest, setDigest] = useState<string | null>(null)

  // If the value changes underneath an open confirmation — the 60s results poll,
  // or another admin moderating the same comment — the captured offsets point
  // into a string that no longer exists. Drop the selection (adjusting state
  // during render, the same idiom the OTP countdown uses); the digest check
  // would reject it server-side anyway.
  // Track hidden_at ALONGSIDE value: hiding does not change the text, so a
  // selection captured before a hide would otherwise survive the round trip and
  // resurface — with a still-valid digest — the moment the comment is restored.
  const [seen, setSeen] = useState({ value: answer.value, hidden: answer.hidden_at ?? null })
  if (seen.value !== answer.value || seen.hidden !== (answer.hidden_at ?? null)) {
    const valueChanged = seen.value !== answer.value
    setSeen({ value: answer.value, hidden: answer.hidden_at ?? null })
    setPending(null)
    setConfirmingHide(false)
    if (valueChanged) setDigest(null)
  }

  useEffect(() => {
    let cancelled = false
    sha256Hex(answer.value).then((hex) => {
      if (!cancelled) setDigest(hex)
    })
    return () => {
      cancelled = true
    }
  }, [answer.value])

  // Translate the live DOM selection into character offsets in the stored value.
  // The selection must sit inside a single un-redacted run — that keeps the
  // mapping unambiguous and stops anyone selecting "through" an existing
  // redaction.
  function captureSelection() {
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed) return
    const startRun = (sel.anchorNode?.parentElement as HTMLElement | null)?.closest('[data-offset]')
    const endRun = (sel.focusNode?.parentElement as HTMLElement | null)?.closest('[data-offset]')
    if (!startRun || startRun !== endRun) return

    const range = sel.getRangeAt(0)
    const base = Number(startRun.getAttribute('data-offset'))
    const start = base + Math.min(range.startOffset, range.endOffset)
    const end = base + Math.max(range.startOffset, range.endOffset)
    if (end <= start) return
    setPending({ start, end, text: answer.value.slice(start, end) })
  }

  // The client never builds the new value — it names a span and proves which
  // text it was looking at. The server does the splice, so "removal only" is
  // structural rather than something the server has to validate its way back to.
  // Offsets go over the wire as CODE POINTS (Ruby's String#[] is character
  // indexed) even though the DOM gives us UTF-16 code units.
  function submitRedaction() {
    if (!pending) return
    router.patch(
      ap(`/surveys/${surveyId}/answers/${answer.id}/redact`),
      {
        start_offset: codePointOffset(answer.value, pending.start),
        end_offset: codePointOffset(answer.value, pending.end),
        value_digest: digest,
      },
      { preserveScroll: true, onFinish: () => setPending(null) }
    )
  }

  function unhide() {
    router.patch(ap(`/surveys/${surveyId}/answers/${answer.id}/unhide`), {}, { preserveScroll: true })
  }

  function hide() {
    router.patch(ap(`/surveys/${surveyId}/answers/${answer.id}/hide`), {}, {
      preserveScroll: true,
      onFinish: () => setConfirmingHide(false),
    })
  }

  if (answer.hidden_at) {
    return (
      <li className="text-sm bg-base-200/50 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
        <span className="iconify lucide--eye-off size-3.5 text-base-content/40" />
        <span className="italic text-base-content/50">
          Hidden from the results, the PDF and the CSV.
        </span>
        <span className="grow" />
        <button type="button" className="btn btn-ghost btn-xs" onClick={unhide}>
          Restore
        </button>
      </li>
    )
  }

  return (
    <li className="text-sm bg-base-200 rounded-lg px-3 py-2 space-y-2">
      <p onMouseUp={captureSelection} onKeyUp={captureSelection}>
        <CommentRuns value={answer.value} moderated={answer.redacted_at != null} />
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-base-content/50">
          Select text in this comment to remove it
        </span>
        <span className="grow" />
        <button type="button" className="btn btn-ghost btn-xs" onClick={() => setConfirmingHide(true)}>
          <span className="iconify lucide--eye-off size-3.5" />
          Hide comment
        </button>
      </div>

      {confirmingHide && (
        <div className="alert alert-soft alert-warning text-sm flex-col items-start gap-2">
          <p>
            Hide this comment from the results, the PDF and the CSV? The response still
            counts toward the totals and scores. This can be undone.
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn btn-warning btn-xs" onClick={hide}>
              Hide comment
            </button>
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => setConfirmingHide(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {pending && (
        <div className="alert alert-soft alert-warning text-sm flex-col items-start gap-2">
          <p>
            Permanently remove <span className="font-semibold">&ldquo;{pending.text}&rdquo;</span>?
            It will be replaced with {REDACTION_TOKEN}. The original wording cannot be recovered.
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn btn-warning btn-xs" onClick={submitRedaction} disabled={!digest}>
              Remove permanently
            </button>
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

function OpenTextResult({ result, surveyId }: { result: QuestionResult; surveyId: number }) {
  // `text_answers` is only sent to owners/admins — its presence IS the
  // permission signal, so members simply get the read-only list.
  const moderatable = result.text_answers
  const texts = result.text_responses || []

  if ((moderatable?.length ?? texts.length) === 0) {
    return <p className="text-sm text-base-content/50">No text responses yet.</p>
  }

  const count = moderatable?.length ?? texts.length

  return (
    <div className="space-y-2">
      <p className="text-sm text-base-content/70">
        {count} {count === 1 ? 'response' : 'responses'}
      </p>
      <ul className="space-y-2 max-h-80 overflow-y-auto">
        {moderatable
          ? moderatable.map((answer) => (
              <RedactableComment key={answer.id} answer={answer} surveyId={surveyId} />
            ))
          : texts.map((text, i) => (
              <li key={i} className="text-sm bg-base-200 rounded-lg px-3 py-2">
                {text}
              </li>
            ))}
      </ul>
    </div>
  )
}

function SingleChoiceResult({ result, primaryColour }: { result: QuestionResult; primaryColour: string }) {
  const { distribution, response_count } = result

  return (
    <div className="space-y-3">
      <p className="text-sm text-base-content/60">
        {response_count} {response_count === 1 ? 'response' : 'responses'}
      </p>
      {distribution && <DistributionChart distribution={distribution} primaryColour={primaryColour} />}
    </div>
  )
}

export default function Results({
  survey,
  question_results,
  total_responses,
  suppressed = false,
  min_display_responses = 3,
  responses_over_time,
  facilitators = [],
  facilitator_filter,
  has_unattributed = false,
  sessions = [],
  session_filter,
  groups = [],
  group_filter,
  individual_responses = null,
  can_see_individual_responses = false,
}: ResultsProps) {
  const [activeTab, setActiveTab] = useState<'aggregate' | 'individual'>('aggregate')
  const [lastUpdated, setLastUpdated] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [copied, setCopied] = useState(false)
  const ap = useAccountPath()
  const surveyUrl = `${window.location.origin}/${survey.slug}`

  // PDF generation state
  const [pdfStatus, setPdfStatus] = useState<PdfStatus>('idle')
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState<string | null>(null)
  const pdfPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const handleGeneratePdf = useCallback(() => {
    setPdfStatus('pending')
    setPdfDownloadUrl(null)
    fetch(ap(`/surveys/${survey.id}/generate_pdf`), {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        'Accept': 'application/json',
      },
    }).then(() => {
      // Start polling for PDF status
      pdfPollRef.current = setInterval(() => {
        fetch(ap(`/surveys/${survey.id}/pdf_status`), {
          headers: { 'Accept': 'application/json' },
        })
          .then((res) => res.json())
          .then((data: { status: string; download_url?: string }) => {
            if (data.status === 'ready' && data.download_url) {
              setPdfStatus('ready')
              setPdfDownloadUrl(data.download_url)
              if (pdfPollRef.current) clearInterval(pdfPollRef.current)
            } else if (data.status === 'failed') {
              // Stop polling — the job exhausted its retries; don't spin forever.
              setPdfStatus('failed')
              if (pdfPollRef.current) clearInterval(pdfPollRef.current)
            }
          })
      }, 2000)
    })
  }, [survey.id, ap])

  // Cleanup PDF polling on unmount
  useEffect(() => {
    return () => {
      if (pdfPollRef.current) clearInterval(pdfPollRef.current)
    }
  }, [])

  // Tick the "last updated" counter every second
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setLastUpdated((prev) => prev + 1)
    }, 1000)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  // Poll every 60s; the "last updated" counter resets when the reload lands
  // (the only source of fresh props — initial state is already 0).
  useEffect(() => {
    pollRef.current = setInterval(() => {
      router.reload({
        only: ['question_results', 'total_responses', 'responses_over_time'],
        onSuccess: () => setLastUpdated(0),
      })
    }, POLL_INTERVAL)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(surveyUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [surveyUrl])

  return (
    <SurveyLayout survey={survey}>
      <div className="space-y-6">
        <PageHeader
          title="Results"
          description={survey.programme_name}
          actions={<>
            {total_responses > 0 && !suppressed && (
              <>
                <a href={ap(`/surveys/${survey.id}/export_csv`)} className="btn btn-ghost btn-sm">
                  <span className="iconify lucide--file-spreadsheet size-4" />
                  CSV
                </a>
                {pdfStatus === 'idle' && (
                  <button onClick={handleGeneratePdf} className="btn btn-ghost btn-sm">
                    <span className="iconify lucide--file-text size-4" />
                    PDF
                  </button>
                )}
                {pdfStatus === 'pending' && (
                  <button disabled className="btn btn-ghost btn-sm">
                    <span className="loading loading-spinner loading-xs" />
                    Generating...
                  </button>
                )}
                {pdfStatus === 'ready' && pdfDownloadUrl && (
                  <a href={pdfDownloadUrl} className="btn btn-primary btn-sm">
                    <span className="iconify lucide--download size-4" />
                    Download PDF
                  </a>
                )}
                {pdfStatus === 'failed' && (
                  <button onClick={handleGeneratePdf} className="btn btn-ghost btn-sm text-error" title="PDF generation failed — try again">
                    <span className="iconify lucide--alert-circle size-4" />
                    Retry PDF
                  </button>
                )}
              </>
            )}
          </>}
        />

        {survey.demo && (
          <div role="alert" className="alert alert-soft alert-info text-sm">
            <span className="iconify lucide--sparkles size-4" />
            <span>
              You're looking at example data from your sample survey — this is what your real
              results will look like.
            </span>
          </div>
        )}

        {/* Filters — always visible when data exists */}
        {(facilitators.length > 0 || groups.length > 0 || sessions.length > 0) && (
          <div className="flex flex-wrap items-center gap-4">
            {facilitators.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-base-content/60">Facilitator:</label>
                {/* The term isn't self-evident to a first-time admin (feedback #8). */}
                <span
                  className="tooltip tooltip-right"
                  data-tip="The person who delivered the programme. Each facilitator gets their own survey link, so responses are attributed without asking participants to name anyone."
                >
                  <span className="iconify lucide--info size-4 text-base-content/40" />
                </span>
                <select
                  className="select select-sm w-auto"
                  value={facilitator_filter ?? ''}
                  onChange={(e) => {
                    const params: Record<string, string> = {}
                    if (e.target.value) params.facilitator = e.target.value
                    if (group_filter) params.group = group_filter
                    if (session_filter) params.session = session_filter
                    router.get(ap(`/surveys/${survey.id}/results`), params, { preserveState: true, preserveScroll: true })
                  }}
                >
                  <option value="">All</option>
                  {facilitators.map((f) => (
                    <option key={f.id} value={f.id}>{f.name} ({f.response_count})</option>
                  ))}
                  {has_unattributed && <option value="unattributed">Unattributed</option>}
                </select>
              </div>
            )}
            {groups.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-base-content/60">Group:</label>
                <select
                  className="select select-sm w-auto"
                  value={group_filter ?? ''}
                  onChange={(e) => {
                    const params: Record<string, string> = {}
                    if (facilitator_filter) params.facilitator = facilitator_filter
                    if (e.target.value) params.group = e.target.value
                    if (session_filter) params.session = session_filter
                    router.get(ap(`/surveys/${survey.id}/results`), params, { preserveState: true, preserveScroll: true })
                  }}
                >
                  <option value="">All</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.id} ({g.response_count})</option>
                  ))}
                  <option value="none">No group</option>
                </select>
              </div>
            )}
            {sessions.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-base-content/60">Session:</label>
                <select
                  className="select select-sm w-auto"
                  value={session_filter ?? ''}
                  onChange={(e) => {
                    const params: Record<string, string> = {}
                    if (facilitator_filter) params.facilitator = facilitator_filter
                    if (group_filter) params.group = group_filter
                    if (e.target.value) params.session = e.target.value
                    router.get(ap(`/surveys/${survey.id}/results`), params, { preserveState: true, preserveScroll: true })
                  }}
                >
                  <option value="">All</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>{s.id} ({s.response_count})</option>
                  ))}
                  <option value="none">No session</option>
                </select>
              </div>
            )}
          </div>
        )}

        {total_responses === 0 ? (
          <div className="card bg-base-100">
            <div className="card-body items-center text-center py-16 space-y-4">
              <span className="iconify lucide--bar-chart-3 size-12 text-base-content/20" />
              <div>
                <h2 className="text-lg font-semibold">No responses yet</h2>
                <p className="text-sm text-base-content/60 mt-1">
                  Share your survey link to start collecting feedback.
                </p>
              </div>
              {survey.published_at && (
                <div className="flex items-center gap-2 mt-4">
                  <code className="text-xs bg-base-200 px-2 py-1 rounded break-all">
                    {surveyUrl}
                  </code>
                  <button onClick={handleCopyLink} className="btn btn-sm btn-primary">
                    <span className={`iconify ${copied ? 'lucide--check' : 'lucide--clipboard'} size-4`} />
                    {copied ? 'Copied' : 'Copy link'}
                  </button>
                  <SurveyQrCode url={surveyUrl} surveyName={survey.name} />
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Tabs — only when admin can see individual responses */}
            {can_see_individual_responses && (
              <div role="tablist" className="tabs tabs-bordered">
                <button
                  role="tab"
                  className={`tab ${activeTab === 'aggregate' ? 'tab-active' : ''}`}
                  onClick={() => setActiveTab('aggregate')}
                >
                  Aggregate
                </button>
                <button
                  role="tab"
                  className={`tab ${activeTab === 'individual' ? 'tab-active' : ''}`}
                  onClick={() => setActiveTab('individual')}
                >
                  Responses ({individual_responses?.length ?? 0})
                </button>
              </div>
            )}

            {activeTab === 'individual' && can_see_individual_responses ? (
              <IndividualResponsesPanel responses={individual_responses ?? []} />
            ) : suppressed ? (
              <SuppressedNotice
                min={min_display_responses}
                total={total_responses}
                filtered={!!(facilitator_filter || session_filter || group_filter)}
              />
            ) : (
            <>
            {/* Stats overview */}
            <div className="stats bg-base-100 w-full">
              <div className="stat">
                <div className="stat-figure text-primary">
                  <span className="iconify lucide--users size-6" />
                </div>
                <div className="stat-title">Total Responses</div>
                <div className="stat-value tabular-nums">{total_responses}</div>
                <div className="stat-desc text-base-content/60">
                  Updated {timeAgo(lastUpdated)}
                </div>
              </div>
              {question_results.length > 0 && (
                <div className="stat">
                  <div className="stat-figure text-primary">
                    <span className="iconify lucide--list-checks size-6" />
                  </div>
                  <div className="stat-title">Questions</div>
                  <div className="stat-value tabular-nums">{question_results.length}</div>
                </div>
              )}
            </div>

            {/* Per-question results */}
            {question_results.map((result, index) => (
              <div key={result.question.id} className="card bg-base-100">
                <div className="card-body space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="text-base font-semibold">
                      {/* Full-strength, not /40 — at that opacity the numbering
                          read as disabled text (feedback #7). */}
                      <span className="text-base-content tabular-nums me-2">{index + 1}.</span>
                      {result.question.question_text}
                      {result.question.required && (
                        <span className="text-error ms-1">*</span>
                      )}
                    </h2>
                    <span className="badge badge-soft badge-sm shrink-0">
                      {questionTypeLabel(result.question)}
                    </span>
                  </div>

                  {result.question.question_type === 'scale' && (
                    result.distribution_before
                      ? <RetrospectiveNumericResult result={result} survey={survey} />
                      : <NumericResult result={result} primaryColour={survey.primary_colour} accentColour={survey.accent_colour} />
                  )}

                  {result.question.question_type === 'open_text' && (
                    <OpenTextResult result={result} surveyId={survey.id} />
                  )}

                  {result.question.question_type === 'single_choice' && (
                    <SingleChoiceResult result={result} primaryColour={survey.primary_colour} />
                  )}
                </div>
              </div>
            ))}

            {/* Responses over time — demoted below the findings (D2). Response
                volume is an operational question ("should I chase a reminder?");
                the per-question results are the analysis, and they own the top
                of the page. */}
            {responses_over_time && responses_over_time.length > 0 && (
              <div className="card bg-base-100">
                <div className="card-body space-y-2">
                  <h2 className="text-base font-semibold">Responses Over Time</h2>
                  <TrendChart data={responses_over_time} primaryColour={survey.primary_colour} />
                </div>
              </div>
            )}
            </>
            )}
          </>
        )}

      </div>
    </SurveyLayout>
  )
}

function IndividualResponsesPanel({ responses }: { responses: IndividualResponse[] }) {
  const [expandedId, setExpandedId] = useState<number | null>(null)

  if (responses.length === 0) {
    return (
      <div className="card bg-base-100">
        <div className="card-body text-center text-base-content/50">
          No responses yet.
        </div>
      </div>
    )
  }

  return (
    <div className="card bg-base-100">
      <div className="card-body">
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Respondent</th>
                <th>Submitted</th>
                <th>Group</th>
                <th>Session</th>
                <th>Facilitator</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {responses.map((r) => (
                <Fragment key={r.id}>
                  <tr className="hover:bg-base-200/50">
                    <td>
                      {r.respondent ? (
                        <div>
                          <div className="font-medium">{[ r.respondent.first_name, r.respondent.last_name ].filter(Boolean).join(' ')}</div>
                          <div className="text-xs text-base-content/50">{r.respondent.email_address}</div>
                        </div>
                      ) : (
                        <span className="text-base-content/40 text-xs">—</span>
                      )}
                    </td>
                    <td className="text-xs text-base-content/60">
                      {new Date(r.submitted_at).toLocaleString()}
                    </td>
                    <td className="text-xs">{r.group_identifier || <span className="text-base-content/30">—</span>}</td>
                    <td className="text-xs">{r.session_identifier || <span className="text-base-content/30">—</span>}</td>
                    <td className="text-xs">{r.facilitator ? [ r.facilitator.first_name, r.facilitator.last_name ].filter(Boolean).join(' ') : <span className="text-base-content/30">—</span>}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-ghost btn-xs"
                        onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                      >
                        {expandedId === r.id ? 'Hide' : 'View'}
                      </button>
                    </td>
                  </tr>
                  {expandedId === r.id && (
                    <tr>
                      <td colSpan={6} className="bg-base-200/30">
                        <div className="space-y-2 py-3">
                          {r.answers.map((a) => (
                            <div key={a.question_id} className="text-sm">
                              <div className="font-medium text-base-content/80">{a.question_text}</div>
                              {a.value_before && (
                                <div className="text-xs text-base-content/60 mt-0.5">Before: <span className="font-mono">{a.value_before}</span></div>
                              )}
                              <div className="text-xs text-base-content/60 mt-0.5">
                                {a.value_before ? 'After: ' : 'Answer: '}
                                <span className="font-mono">{a.value || <span className="italic">no answer</span>}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
